import { useEffect, useId, useRef, useState, type RefObject } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, ApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type { MessageKey } from "../lib/messages";
import { useRequireAuth, useSession } from "../lib/session";
import { useAsync } from "../lib/useAsync";
import { IconCall, IconChat, IconCheck, IconChecklist, IconFlag, IconNext, IconShare, IconShield, IconStore, IconTruck, IconVerified, IconWhatsApp, IconX } from "../ui/icons";
import {
  AsyncButton, ConfirmDialog, Grade, GradeGuide, ErrorState, HeartButton, Loading, MoneyInput, Notice, PriceGuideBar, SpecList, TrustBadges, TrustLine, VerdictTag, WarrantyText, useToast,
  type PriceGuide, type PublicSpec, type Trust, type Verdict, type Warranty,
} from "../ui/kit";

type Other = { id: string; priceCents: number; currency: string; condition: string; city: string; deliveryAvailable: boolean; seller: { displayName: string; verified: boolean }; coverUrl: string | null };
type Detail = {
  id: string;
  status: string;
  title: string;
  category: string;
  brand: string;
  productId: string;
  specs: PublicSpec[];
  condition: string;
  defects: string;
  accessories: string[];
  description: string;
  priceCents: number;
  wasCents: number | null;
  negotiable: boolean;
  newPriceCents: number | null;
  savingsPct: number | null;
  currency: string;
  quantityAvailable: number;
  city: string;
  pickupAvailable: boolean;
  deliveryAvailable: boolean;
  deliveryFeeCents: number;
  deliveryDays: number;
  aiAssisted: boolean;
  imeiRecorded?: boolean;
  warranty?: Warranty;
  photos: { id: string; url: string; thumbUrl: string; view: string }[];
  seller: { id: string; displayName: string; verified: boolean; city: string; memberSince: string; rating: { average: number | null; count: number }; trust: Trust; business?: { name: string } | null };
  contact: { chat: boolean; whatsapp: boolean; calls: boolean };
  priceGuide: PriceGuide | null;
  priceVerdict: Verdict | null;
  viewCount: number;
  otherSellers: Other[];
};
type Mine = { favorite: boolean; isOwner: boolean; deal: { id: string; status: string } | null };
type Reveal = { method: "whatsapp" | "call"; phone: string; numberVerified: boolean; whatsappUrl: string | null; telUrl: string | null; dealId: string };

const PICKER_CONDITIONS = ["new", "like_new", "good", "fair"] as const;

export function ListingPage() {
  const { id } = useParams();
  const { t, money } = useI18n();
  const { me } = useSession();
  const requireAuth = useRequireAuth();
  const nav = useNavigate();
  const toast = useToast();
  const { data: l, error, loading, reload } = useAsync(() => api<Detail>(`/api/catalog/listings/${id}`), [id]);
  const mine = useAsync(() => api<Mine>(`/api/catalog/listings/${id}/me`), [id, me?.id]);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState("scam");
  const [details, setDetails] = useState("");
  const [asking, setAsking] = useState(false);
  const [called, setCalled] = useState<Reveal | null>(null);
  const [contactError, setContactError] = useState<string | null>(null);
  const gallery = useRef<HTMLDivElement>(null);
  const safety = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (l) document.title = `${l.title} · Walashy`;
  }, [l]);
  // Room at the bottom of the page for the pinned contact bar on phones.
  useEffect(() => {
    document.body.classList.add("has-buybar");
    return () => document.body.classList.remove("has-buybar");
  }, []);
  useEffect(() => {
    setPhotoIdx(0);
    setCalled(null);
    setContactError(null);
    // Counts once per visitor per day on the server; the seller's own visits never count.
    if (id) api(`/api/listings/${id}/track`, { body: { kind: "view" } }).catch(() => {});
  }, [id]);

  if (loading && !l) return <Loading />;
  if (error || !l) return <ErrorState error={error} onRetry={reload} />;

  const soldOut = l.status === "sold_out" || l.quantityAvailable === 0 && l.status !== "reserved";
  const reserved = l.status === "reserved";
  const isOwner = mine.data?.isOwner;
  const deal = mine.data?.deal;
  const canContact = !isOwner && !soldOut;
  const battery = l.specs.find((s) => s.key === "battery_health")?.value;

  // Condition picker: this phone from every seller, cheapest price per condition.
  const byCondition = new Map<string, { id: string; priceCents: number }>();
  for (const o of [{ id: l.id, priceCents: l.priceCents, condition: l.condition }, ...l.otherSellers]) {
    const cur = byCondition.get(o.condition);
    if (o.condition === l.condition) byCondition.set(o.condition, { id: l.id, priceCents: l.priceCents });
    else if (!cur || o.priceCents < cur.priceCents) byCondition.set(o.condition, { id: o.id, priceCents: o.priceCents });
  }
  const pickerConditions = PICKER_CONDITIONS.filter((c) => c !== "new" || byCondition.has("new"));
  if (!pickerConditions.includes(l.condition as (typeof PICKER_CONDITIONS)[number])) pickerConditions.push(l.condition as (typeof PICKER_CONDITIONS)[number]);

  const openDeal = deal && ["open", "agreed", "reserved"].includes(deal.status);
  const startChat = () => requireAuth(() => (openDeal ? nav(`/inbox/${deal!.id}`) : setAsking(true)));

  // WhatsApp and calls: the number is fetched only when tapped, and the tap is what opens a tracked deal.
  const reveal = async (method: "whatsapp" | "call") => {
    if (!requireAuth()) return;
    setContactError(null);
    // Open the WhatsApp tab during the tap itself, so pop-up blockers allow it; it's pointed at WhatsApp below.
    const tab = method === "whatsapp" ? window.open("", "_blank") : null;
    try {
      const r = await api<Reveal>(`/api/listings/${l.id}/contact`, { body: { method } });
      if (method === "whatsapp" && r.whatsappUrl) {
        if (tab) tab.location.href = r.whatsappUrl;
        else window.location.href = r.whatsappUrl;
      } else setCalled(r);
      mine.reload();
    } catch (e) {
      tab?.close();
      setContactError((e as ApiError).message);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/l/${l.id}`;
    api(`/api/listings/${l.id}/track`, { body: { kind: "share" } }).catch(() => {});
    try {
      if (navigator.share) await navigator.share({ title: l.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast(t("listing.linkCopied"));
      }
    } catch {
      /* the person closed the share sheet */
    }
  };

  const showPhoto = (i: number) => {
    gallery.current?.children[i]?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    setPhotoIdx(i);
  };
  const viewName = (v: string) => t(`view.${v}` as MessageKey);

  return (
    <div>
      <nav className="crumbs" aria-label={t("nav.browse")}>
        <Link to="/">{t("nav.browse")}</Link>
        <IconNext size={14} />
        <Link to={`/?brand=${encodeURIComponent(l.brand)}`}>{l.brand}</Link>
        <IconNext size={14} />
        <span aria-current="page" dir="auto">{l.title}</span>
      </nav>

      <div className="listing">
        <section className="gallery-area" aria-roledescription="carousel" aria-label={l.title}>
          <div className="gallery-main">
            <div
              ref={gallery}
              className="gallery"
              onScroll={(e) => {
                const el = e.currentTarget;
                setPhotoIdx(Math.round(Math.abs(el.scrollLeft) / el.clientWidth));
              }}
              tabIndex={0}
              aria-label={t("listing.photo", { n: photoIdx + 1, v: viewName(l.photos[photoIdx]?.view ?? "front") })}
            >
              {l.photos.map((p, i) => (
                <img key={p.id} src={p.url} alt={t("listing.photo", { n: i + 1, v: viewName(p.view) })} loading={i ? "lazy" : "eager"} />
              ))}
            </div>
            {!isOwner && <HeartButton id={l.id} title={l.title} />}
            <span className="gallery-count" aria-hidden>{photoIdx + 1} / {l.photos.length}</span>
          </div>
          <div className="dots" aria-hidden>{l.photos.map((p, i) => <span key={p.id} className={i === photoIdx ? "on" : ""} />)}</div>
          {l.photos.length > 1 && (
            <div className="gallery-thumbs">
              {l.photos.map((p, i) => (
                <button key={p.id} aria-current={i === photoIdx} aria-label={t("listing.photo", { n: i + 1, v: viewName(p.view) })} onClick={() => showPhoto(i)}>
                  <img src={p.thumbUrl} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </section>

        <aside className="buy-area">
          <div className="buy-box">
            <div>
              <h1 dir="auto">{l.title}</h1>
              {battery && <p className="sub">{t("listing.battery", { b: battery })}</p>}
            </div>

            <div className="price-block">
              <span className="price-xl">{money(l.priceCents, l.currency)}</span>
              {l.wasCents ? <span className="was-line">{t("listing.was", { p: money(l.wasCents, l.currency) })}</span> : null}
              <span className="row wrap" style={{ gap: 6, marginTop: 6 }}>
                <VerdictTag v={l.priceVerdict} />
                <span className="tag">{t(l.negotiable ? "listing.negotiable" : "listing.fixed")}</span>
                {l.savingsPct && l.newPriceCents ? <span className="save small">{t("card.saveVsNew", { p: l.savingsPct })}</span> : null}
              </span>
              {reserved && <span className="tag offer" style={{ marginTop: 6 }}>{t("listing.reserved")}</span>}
              {soldOut && <span className="tag bad" style={{ marginTop: 6 }}>{t("listing.soldOut")}</span>}
            </div>

            {l.priceGuide && <PriceGuideBar guide={l.priceGuide} priceCents={l.priceCents} currency={l.currency} label={t("guide.forThis")} />}

            <div>
              <div className="block-label"><b>{t("filters.condition")}</b><GradeGuide /></div>
              <div className="cond-picker" role="list">
                {pickerConditions.map((c) => {
                  const hit = byCondition.get(c);
                  const current = c === l.condition;
                  const label = <><b><Grade c={c} /> {t(`cond.${c}` as MessageKey)}</b><span>{hit ? (current ? money(hit.priceCents, l.currency) : t("listing.condFrom", { p: money(hit.priceCents, l.currency) })) : t("listing.condNone")}</span></>;
                  if (current) return <span key={c} role="listitem" className="cond-option" aria-current="true">{label}</span>;
                  if (!hit) return <span key={c} role="listitem" className="cond-option" aria-disabled="true">{label}</span>;
                  return <Link key={c} role="listitem" className="cond-option" to={`/l/${hit.id}`}>{label}</Link>;
                })}
              </div>
              <p className="tiny muted" style={{ marginTop: "var(--s2)" }} dir="auto">{l.defects || t("listing.noDefects")}</p>
            </div>

            {canContact && (
              <div className="actions contact-actions">
                {reserved && !openDeal && <Notice>{t("listing.reservedHint")}</Notice>}
                <button className="btn primary block lg" onClick={startChat}>
                  <IconChat size={20} /> {openDeal ? t("listing.continueDeal") : t("listing.message")}
                </button>
                {(l.contact.whatsapp || l.contact.calls) && (
                  <div className="row">
                    {l.contact.whatsapp && (
                      <button className="btn wa grow" onClick={() => reveal("whatsapp")}>
                        <IconWhatsApp size={20} /> {t("contact.whatsapp")}
                      </button>
                    )}
                    {l.contact.calls && (
                      <button className="btn grow" onClick={() => reveal("call")}>
                        <IconCall size={20} /> {t("contact.call")}
                      </button>
                    )}
                  </div>
                )}
                {called && (
                  <div className="reveal card">
                    <a className="reveal-number num" href={called.telUrl ?? `tel:${called.phone}`} dir="ltr">{called.phone}</a>
                    <span className="tiny muted">{t(called.numberVerified ? "contact.numberVerified" : "contact.numberUnverified")} · {t("contact.dealSaved")}</span>
                  </div>
                )}
                {contactError && <Notice kind="bad">{contactError}</Notice>}
              </div>
            )}
            {soldOut && !isOwner && <Link className="btn block" to={`/?q=${encodeURIComponent(l.title)}`}>{t("listing.findSimilar")}</Link>}
            {isOwner && (
              <>
                <div className="notice">{t("listing.yours")}</div>
                <Link className="btn block" to={`/sell/${l.id}/manage`}>{t("listing.manage")}</Link>
              </>
            )}

            <div className="info-rows">
              <div className="info-row">
                {l.deliveryAvailable ? <IconTruck /> : <IconStore />}
                <span className="t">
                  <b>{t("listing.meetIn", { city: l.city })}</b>
                  <span>{l.deliveryAvailable ? t("listing.canDeliver", { fee: l.deliveryFeeCents ? money(l.deliveryFeeCents, l.currency) : t("listing.freeDelivery") }) : t("listing.meetOnly")}</span>
                </span>
              </div>
              <div className="info-row">
                <span className="avatar" aria-hidden>{l.seller.displayName.slice(0, 1)}</span>
                <span className="t">
                  <Link to={`/s/${l.seller.id}`} className="seller-link"><b dir="auto">{l.seller.displayName} {l.seller.verified && <IconVerified size={15} className="save" />}</b></Link>
                  <span><TrustLine trust={l.seller.trust} /></span>
                  {l.seller.business && <span><IconStore size={14} /> {t("shop.registered")}</span>}
                  <TrustBadges trust={l.seller.trust} compact />
                  <Link to={`/s/${l.seller.id}`} className="link small">{t("seller.allListings")}</Link>
                </span>
              </div>
            </div>

            <div className="protect">
              <IconShield size={18} />
              <span>
                {t("listing.direct")}{" "}
                <button type="button" className="link" onClick={() => safety.current?.showModal()} aria-haspopup="dialog">{t("safety.open")}</button>
              </span>
            </div>
            {l.imeiRecorded && <div className="protect"><IconCheck size={18} /> <span>{t("imei.recorded")}</span></div>}
            {l.warranty && <div className="protect"><IconShield size={18} /> <span><WarrantyText w={l.warranty} /></span></div>}
          </div>
        </aside>

        <div className="details-area stack-lg">
          <SpecList specs={l.specs} aiAssisted={l.aiAssisted} />

          <section className="detail-block" aria-labelledby="details-title">
            <h2 id="details-title">{t("listing.details")}</h2>
            <dl className="detail-pairs">
              <div><dt>{t("listing.condition")}</dt><dd><span className="row"><Grade c={l.condition} /> {t(`cond.${l.condition}` as MessageKey)}, {t(`cond.${l.condition}.hint` as MessageKey)}</span></dd></div>
              <div><dt>{t("listing.defects")}</dt><dd dir="auto">{l.defects || t("listing.noDefects")}</dd></div>
              {l.accessories.length > 0 && <div><dt>{t("listing.accessories")}</dt><dd dir="auto">{l.accessories.join("، ")}</dd></div>}
              {l.description && <div><dt>{t("listing.description")}</dt><dd dir="auto" style={{ whiteSpace: "pre-wrap" }}>{l.description}</dd></div>}
            </dl>
          </section>

          {l.otherSellers.length > 0 && (
            <section className="detail-block" aria-labelledby="compare-title">
              <div>
                <h2 id="compare-title">{t("listing.compare")}</h2>
                <p className="small muted">{t("listing.compareHint")}</p>
              </div>
              <div className="compare-list">
                {l.otherSellers.map((o) => (
                  <Link key={o.id} to={`/l/${o.id}`} className="compare-item">
                    {o.coverUrl ? <img src={o.coverUrl} alt="" loading="lazy" /> : <span className="ph" />}
                    <span className="who">
                      <strong dir="auto">{o.seller.displayName}</strong>
                      <span className="row" style={{ gap: 6 }}><Grade c={o.condition} /> {t(`cond.${o.condition}` as MessageKey)}، <span dir="auto">{o.city}</span></span>
                    </span>
                    <span className="price">{money(o.priceCents, o.currency)}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div className="text-actions">
            <button className="btn ghost sm" onClick={share}>
              <IconShare size={18} /> {t("listing.share")}
            </button>
            {!isOwner && (
              <button className="btn ghost sm" onClick={() => requireAuth(() => setReporting(true))}>
                <IconFlag size={18} /> {t("listing.report")}
              </button>
            )}
            {l.viewCount > 9 && <span className="tiny muted">{t("listing.views", { n: l.viewCount })}</span>}
          </div>
        </div>
      </div>

      {canContact && (
        <div className="buybar">
          <span className="p"><b>{money(l.priceCents, l.currency)}</b><span>{t("listing.noFees")}</span></span>
          {l.contact.whatsapp && (
            <button className="btn wa" onClick={() => reveal("whatsapp")} aria-label={t("contact.whatsapp")}><IconWhatsApp size={20} /></button>
          )}
          <button className="btn primary block" onClick={startChat}>{openDeal ? t("listing.continueDeal") : t("listing.message")}</button>
        </div>
      )}

      {asking && <AskSeller listing={l} onClose={() => setAsking(false)} onStarted={(dealId) => nav(`/inbox/${dealId}`)} />}
      <SafetySheet sheet={safety} />

      <ConfirmDialog
        open={reporting}
        title={t("report.title")}
        confirmLabel={t("report.send")}
        onClose={() => setReporting(false)}
        onConfirm={async () => {
          await api("/api/reports", { body: { targetType: "listing", targetId: l.id, reason, details } });
          toast(t("common.reported"));
        }}
        body={
          <div className="stack">
            <div className="stack" role="radiogroup">
              {(["scam", "stolen", "counterfeit", "wrong_info", "other"] as const).map((r) => (
                <button key={r} type="button" role="radio" className="choice" aria-checked={reason === r} onClick={() => setReason(r)}>
                  <strong>{t(`report.${r}`)}</strong>
                </button>
              ))}
            </div>
            <label className="label" htmlFor="report-details">{t("report.details")}</label>
            <textarea id="report-details" className="input" value={details} onChange={(e) => setDetails(e.target.value)} maxLength={1000} dir="auto" />
          </div>
        }
      />
    </div>
  );
}


const QUICK = ["ask.available", "ask.seeToday", "ask.battery", "ask.bestPrice"] as const;

/** First contact: a ready question in one tap, and optionally a price, without leaving the listing. */
function AskSeller({ listing, onClose, onStarted }: { listing: Detail; onClose: () => void; onStarted: (dealId: string) => void }) {
  const { t, money } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [text, setText] = useState(t("ask.available"));
  const [price, setPrice] = useState<number | null>(null);
  const [withPrice, setWithPrice] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    ref.current?.showModal();
  }, []);
  const send = async () => {
    setErr(null);
    try {
      const c = await api<{ id: string }>("/api/conversations", { body: { listingId: listing.id, message: text.trim() || t("ask.available") } });
      if (withPrice && price) await api(`/api/conversations/${c.id}/proposals`, { body: { priceCents: price, quantity: 1 } });
      onStarted(c.id);
    } catch (e) {
      setErr((e as ApiError).message);
    }
  };
  return (
    <dialog ref={ref} className="sheet" aria-labelledby={titleId} onClose={onClose}>
      <div className="sheet-head">
        <button className="icon-btn" onClick={() => ref.current?.close()} aria-label={t("common.close")}><IconX /></button>
        <h2 id={titleId}>{t("ask.title", { name: listing.seller.displayName })}</h2>
      </div>
      <div className="sheet-body stack">
        <div className="chips" style={{ flexWrap: "wrap" }}>
          {QUICK.map((k) => (
            <button key={k} type="button" className="chip" aria-pressed={text === t(k)} onClick={() => setText(t(k))}>{t(k)}</button>
          ))}
        </div>
        <label className="label" htmlFor="ask-text">{t("ask.message")}</label>
        <textarea id="ask-text" className="input" value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} dir="auto" />
        {listing.negotiable && (
          <div className="card stack">
            <label className="row" style={{ cursor: "pointer" }}>
              <input type="checkbox" checked={withPrice} onChange={(e) => setWithPrice(e.target.checked)} style={{ width: 20, height: 20 }} />
              <span>{t("ask.withPrice")}</span>
            </label>
            {withPrice && (
              <>
                <MoneyInput value={price} onChange={setPrice} currency={listing.currency} />
                <span className="tiny muted">
                  {t("ask.asking", { p: money(listing.priceCents, listing.currency) })}
                  {listing.priceGuide ? ` · ${t("guide.typical", { p: money(listing.priceGuide.typicalCents, listing.currency) })}` : ""}
                </span>
              </>
            )}
          </div>
        )}
        <p className="tiny muted">{t("ask.note")}</p>
        {err && <Notice kind="bad">{err}</Notice>}
      </div>
      <div className="sheet-foot">
        <span />
        <AsyncButton className="btn primary" onClick={send} disabled={withPrice && !price}>{t("ask.send")}</AsyncButton>
      </div>
    </dialog>
  );
}

const SAFETY = ["safety.public", "safety.imei", "safety.accounts", "safety.test", "safety.payAfter", "safety.receipt"] as const;

/** How to buy safely from a stranger, in six lines. Walashy doesn't hold money, so this is how it protects people. */
export function SafetySheet({ sheet }: { sheet: RefObject<HTMLDialogElement | null> }) {
  const { t } = useI18n();
  const titleId = useId();
  return (
    <dialog ref={sheet} className="sheet" aria-labelledby={titleId}>
      <div className="sheet-head">
        <button className="icon-btn" onClick={() => sheet.current?.close()} aria-label={t("common.close")}><IconX /></button>
        <h2 id={titleId}>{t("safety.title")}</h2>
      </div>
      <div className="sheet-body">
        <p className="muted">{t("safety.intro")}</p>
        <ol className="safety-list">
          {SAFETY.map((k) => (
            <li key={k}><IconChecklist size={18} /> <span>{t(k)}</span></li>
          ))}
        </ol>
      </div>
    </dialog>
  );
}
